import React, { useContext, useState } from "react";
import { Box, ChakraProvider } from "@chakra-ui/react";
import { useAtom } from "jotai";
import { DataGet } from "./components/DataGet";
import {
  DataContextProvider,
  LikeButtonFlagContextProvider,
  ModalContextProvider,
  RenderContext,
  RenderContextProvider,
} from "./components/CreateContext";
import { currentMenuNumAtom } from "./components/atoms/currentMenuState";
import GridPatternUsageInfo from "./components/GridPatternUsageInfo";
import { Clock } from "./components/DataCard/Gadget/Clock";
import MenuWithLogo from "./components/DataCard/Gadget/MenuWithLogo";
import styles from "./css/nonScrollbar.module.scss";
import SnapFlexGrid from "./components/SnapFlexGrid";
import ScrollFlexGrid from "./components/ScrollFlexGrid";
import CampusMap from "./components/CampusMap";
import EventCalender from "./components/EventCalender";
import InactivityTimeout from "./components/utils/InactivityTimeout";
import Warning from "./components/Warning";

/**
 * Appコンポーネント
 *
 * このコンポーネントは、アプリケーションのルートコンポーネントです。
 * アプリケーションの全体的なレイアウトを設定し、`REACT_APP_SHOW_MULTI_MENU` 環境変数に基づいて表示するコンテンツを切り替えます。
 *
 * - `REACT_APP_SHOW_MULTI_MENU` が "true" の場合、メニュー機能を持ったShareTilesが表示されます。
 * - `REACT_APP_SHOW_MULTI_MENU` が "false" の場合、メニュー機能を持たないShareTilesが表示されます。。
 * - 環境変数(`REACT_APP_SHOW_MULTI_MENU`)が設定されていないか、不正な場合にはエラーメッセージを表示します。
 *
 * また、現在のメニュー番号に基づいて、表示するコンポーネント（`GridPatternUsageInfo`、`SnapFlexGrid`、`ScrollFlexGrid`など）を切り替えます。
 * ヘッダー部分には`Clock`コンポーネントが表示され、メニューによって表示される内容は動的に決まります。
 */

export default function App() {
  const [currentMenuNum] = useAtom(currentMenuNumAtom);
  const { settings } = useContext(RenderContext);
  const [isPortrait, setIsPortrait] = useState(
    window.innerHeight > window.innerWidth
  ); //Menu機能を持つShareTilesの縦横判定のため使用
  const topicTransitionMethod = settings.find(
    (setting) => setting.KEY_NAME === "topic_transition_method"
  );
  const layout =
    typeof topicTransitionMethod?.VALUE === "string"
      ? topicTransitionMethod.VALUE
      : "snap";

  // ページ全体は動かせないように固定
  const fixedPageStyles: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    touchAction: "none",
  };

  // cursorがtrueかどうか調べる
  const searchParams = new URLSearchParams(window.location.search);
  const cursor = searchParams.get("cursor");

  // 右クリックを禁止する
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // ドラッグを禁止する
  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
  };

  if (process.env.REACT_APP_SHOW_MULTI_MENU === "true") {
    return (
      <ChakraProvider>
        <RenderContextProvider>
          <ModalContextProvider>
            <DataContextProvider>
              <LikeButtonFlagContextProvider>
                <InactivityTimeout />
                <div
                  style={{ cursor: cursor == "true" ? "true" : "none" }}
                  onContextMenu={handleContextMenu}
                  onDragStart={handleDragStart}
                >
                  <Box h="100vh" overflow="hidden" style={fixedPageStyles}>
                    {/* ヘッダー*/}
                    <Clock length={2} />

                    {/* 縦長画面の場合に警告表示 */}
                    {isPortrait && (
                      <Warning>画面を横長にしてご利用ください</Warning>
                    )}

                    {layout == "scroll" && !isPortrait && (
                      <Box
                        h="100%"
                        className={styles.nonScrollbar}
                        flexDirection={
                          window.innerHeight > window.innerWidth
                            ? "column"
                            : "row"
                        }
                        style={{ position: "relative" }}
                      >
                        {(() => {
                          switch (currentMenuNum) {
                            case 0:
                              return <CampusMap />;
                            case 1:
                              return <EventCalender />;
                            case 2:
                              return (
                                <ScrollFlexGrid categoryId="university_news" />
                              );
                            case 3:
                              return <ScrollFlexGrid categoryId="instagram" />;
                            case 4:
                              return <ScrollFlexGrid categoryId="twitter" />;
                            case 5:
                              return <ScrollFlexGrid categoryId="youtube" />;
                            case 6:
                              return <GridPatternUsageInfo />;
                          }
                        })()}
                      </Box>
                    )}
                    {layout == "snap" && !isPortrait && (
                      <Box
                        h="100%"
                        className={styles.nonScrollbar}
                        flexDirection={
                          window.innerHeight > window.innerWidth
                            ? "column"
                            : "row"
                        }
                        style={{ position: "relative" }}
                      >
                        {(() => {
                          switch (currentMenuNum) {
                            case 0:
                              return <CampusMap />;
                            case 1:
                              return <EventCalender />;
                            case 2:
                              return (
                                <SnapFlexGrid categoryId="university_news" />
                              );
                            case 3:
                              return <SnapFlexGrid categoryId="instagram" />;
                            case 4:
                              return <SnapFlexGrid categoryId="twitter" />;
                            case 5:
                              return <SnapFlexGrid categoryId="youtube" />;
                            case 6:
                              return <GridPatternUsageInfo />;
                          }
                        })()}
                        <Box
                          position="absolute"
                          left={
                            window.innerHeight > window.innerWidth
                              ? "0%"
                              : "80%"
                          }
                          w={
                            window.innerHeight > window.innerWidth
                              ? "100%"
                              : "20%"
                          }
                          zIndex="10"
                          bg="rgba(249, 250, 251, 0.9)"
                          backdropFilter="blur(1px)"
                        >
                          <MenuWithLogo />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </div>
              </LikeButtonFlagContextProvider>
            </DataContextProvider>
          </ModalContextProvider>
        </RenderContextProvider>
      </ChakraProvider>
    );
  } else if (process.env.REACT_APP_SHOW_MULTI_MENU === "false") {
    return (
      <ChakraProvider>
        <RenderContextProvider>
          <DataGet />
        </RenderContextProvider>
      </ChakraProvider>
    );
  } else {
    console.log(
      "環境変数のREACT_APP_SHOW_MULTI_MENUが設定されていないか、不正です"
    );
    return null;
  }
}
